import * as React from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";

/**
 * Open: fade + settle from 98% over 200 ms. Close: fade + shrink over 150 ms.
 * Radix holds data-state="closed" on the content until the animation ends,
 * so the exit is real, not a cut. The slide classes restate the -50% centring
 * because tailwindcss-animate's keyframes replace the element's transform.
 */
const DIALOG_MOTION =
  "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] " +
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[0.985] data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] " +
  "data-[state=open]:duration-200 data-[state=closed]:duration-150 ease-out motion-reduce:animate-none";

// Main Dialog Root
export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogPortal = RadixDialog.Portal;

// Overlay with theme support
export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Overlay>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Overlay>
>(({ className, ...props }, ref) => (
  <RadixDialog.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 backdrop-blur-sm",
      "bg-[var(--lex-overlay-backdrop)]",
      // Radix keeps the overlay mounted through data-state=closed so the exit plays.
      "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      "data-[state=open]:duration-200 data-[state=closed]:duration-150 motion-reduce:animate-none",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = RadixDialog.Overlay.displayName;

// Dialog Content
type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof RadixDialog.Content
> & {
  overlayClassName?: string;
};

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  DialogContentProps
>(({ className, children, overlayClassName, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <RadixDialog.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl p-6 shadow-lg focus:outline-none border",
        "bg-[var(--lex-surface-card)] border-[var(--lex-border)]",
        DIALOG_MOTION,
        className,
      )}
      {...props}
    >
      {children}
    </RadixDialog.Content>
  </DialogPortal>
));
DialogContent.displayName = RadixDialog.Content.displayName;

// Dialog Content (Large variant)
type DialogContentLargeProps = React.ComponentPropsWithoutRef<
  typeof RadixDialog.Content
> & {
  overlayClassName?: string;
};

export const DialogContentLarge = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Content>,
  DialogContentLargeProps
>(({ className, children, overlayClassName, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <RadixDialog.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-50 w-full max-w-5xl h-[80vh] max-h-[700px] -translate-x-1/2 -translate-y-1/2 rounded-xl p-0 shadow-lg focus:outline-none border",
        "bg-[var(--lex-surface-card)] border-[var(--lex-border)]",
        DIALOG_MOTION,
        className,
      )}
      {...props}
    >
      {children}
    </RadixDialog.Content>
  </DialogPortal>
));
DialogContentLarge.displayName = "DialogContentLarge";

// Dialog Header
export const DialogHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cn("mb-4 flex flex-col gap-2", className)} {...props} />;

// Dialog Title
export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Title>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, ...props }, ref) => (
  <RadixDialog.Title
    ref={ref}
    className={cn(
      "text-lg font-bold leading-tight tracking-tight text-[var(--lex-text-primary)]",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = RadixDialog.Title.displayName;

// Dialog Description
export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Description>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, ...props }, ref) => (
  <RadixDialog.Description
    ref={ref}
    className={cn("text-sm text-[var(--lex-text-tertiary)]", className)}
    {...props}
  />
));
DialogDescription.displayName = RadixDialog.Description.displayName;

// Dialog Close Button
export const DialogClose = RadixDialog.Close;
